// src/components/TosUpdateModal.js
// ============================================================================
// TERMS UPDATE / "WHAT'S NEW" MODAL
//
// Shown once to a returning member whose accepted Terms version is behind
// CURRENT_TOS_VERSION. Framed as a patch note first, acceptance second — the
// member sees what they GAINED, with the terms summary underneath, and can
// open the full Terms before agreeing.
//
// Why this exists: bumping the Terms version used to re-prompt NOBODY —
// nothing compared a stored verification.tos.version against the current one,
// so clauses only ever bound new signups. This closes that.
//
// Acceptance is logged server-side by the existing acceptTos callable
// (functions/modules/auth.js), which stamps the version + an audit trail.
// Copy lives in constants/tosUpdate.js and is founder-approved before deploy.
//
// A save that fails on OUR side (PLATFORM_ERROR_CODES) shows a short notice,
// then calls onDismissForSession so the prompt steps aside for the session;
// any other failure stays up with its message so the member can retry.
//
// v1.5 (evergreen 3.5a), v1.6 (tiers): the lists are CUMULATIVE.
// acceptedVersion is the stored verification.tos.version; the v1.6 lists
// always show, plus the v1.5 lists when it is below 1.5 and the v1.4 lists
// when it is below 1.4 (both when missing or unparseable;
// tosUpdateListsFor). A
// quiet "Manage your plan or memberships" link (onManageBilling) opens Your
// memberships or Your plan, both exempt from this prompt, so no cancel ever
// waits on accepting new Terms (design 13.8).
//
// v1.7 (evergreen 3.5b): four tiers. The v1.7 (team) lists always show, the
// v1.6 lists when the stored version is below 1.6, the v1.5 lists below 1.5,
// the v1.4 lists below 1.4 (tosUpdateListsFor). The manage link keys on
// anyEverLaunched (channel OR team memberships ever launched), so a team
// member whose channel side never launched still reaches Your memberships
// (3.5b design 13.12, 15.2).
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, Modal, ActivityIndicator, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  TOS_UPDATE, CURRENT_TOS_VERSION, TOS_ACCEPT_UNAVAILABLE, tosUpdateListsFor, manageBillingLinkFor,
} from '../constants/tosUpdate';
import useMembershipsLive from '../hooks/useMembershipsLive';

// Callable error codes that mean the platform failed, not the member:
// function crashed / not deployed / unreachable / timed out. The client SDK
// prefixes them ('functions/internal'); both forms are accepted.
const PLATFORM_ERROR_CODES = new Set(['internal', 'unavailable', 'deadline-exceeded', 'not-found']);
// How long the notice stays up before the prompt steps aside.
const PLATFORM_ERROR_DISMISS_MS = 3500;

const C = {
  BG: 'rgba(0,0,0,0.88)',
  CARD: '#111',
  BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  MUTED: '#888',
};

export default function TosUpdateModal({
  visible, acceptedVersion, onAccepted, onReadFullTerms, onDismissForSession, onManageBilling,
}) {
  const { anyEverLaunched } = useMembershipsLive();
  const lists = tosUpdateListsFor(acceptedVersion);
  const manageLink = manageBillingLinkFor(anyEverLaunched);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // True while the platform-failure notice is up, before the prompt steps aside.
  const [dismissing, setDismissing] = useState(false);
  const dismissTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(dismissTimerRef.current), []);

  const handleAccept = async () => {
    setSaving(true);
    setError(null);
    try {
      const acceptTos = httpsCallable(getFunctions(), 'acceptTos');
      await acceptTos({ version: CURRENT_TOS_VERSION });
      onAccepted?.();
    } catch (e) {
      const code = String(e?.code || '').replace(/^functions\//, '');
      if (PLATFORM_ERROR_CODES.has(code) && onDismissForSession) {
        // Our failure, not theirs: say so, then get out of the way for the
        // session. The prompt comes back next launch if the write never landed.
        setError(TOS_ACCEPT_UNAVAILABLE);
        setDismissing(true);
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = setTimeout(() => {
          dismissTimerRef.current = null;
          setError(null);
          setDismissing(false);
          onDismissForSession();
        }, PLATFORM_ERROR_DISMISS_MS);
        return;
      }
      // Never trap the member behind a failed write — surface it and let them
      // retry. The prompt reappears next launch if the write never lands.
      setError(e?.message || 'Could not save that. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => {}}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
            <Text style={styles.heading}>{TOS_UPDATE.heading}</Text>
            <Text style={styles.intro}>{TOS_UPDATE.intro}</Text>

            {lists.highlights.map((h) => (
              <View key={h.title} style={styles.highlight}>
                <Ionicons name={h.icon} size={20} color={C.GOLD} style={styles.highlightIcon} />
                <View style={styles.highlightText}>
                  <Text style={styles.highlightTitle}>{h.title}</Text>
                  <Text style={styles.highlightBody}>{h.body}</Text>
                </View>
              </View>
            ))}

            <View style={styles.divider} />

            <Text style={styles.termsHeading}>{TOS_UPDATE.termsHeading}</Text>
            <Text style={styles.termsIntro}>{TOS_UPDATE.termsIntro}</Text>
            {lists.termsPoints.map((p) => (
              <Text key={p} style={styles.termsPoint}>• {p}</Text>
            ))}

            <TouchableOpacity onPress={onReadFullTerms} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Text style={styles.readFull}>{TOS_UPDATE.readFullLabel}</Text>
            </TouchableOpacity>
            {onManageBilling ? (
              <TouchableOpacity
                onPress={() => onManageBilling(manageLink.route)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="link"
              >
                <Text style={styles.manageLink}>{manageLink.label}</Text>
              </TouchableOpacity>
            ) : null}
          </ScrollView>

          {error ? <Text style={styles.error}>{error}</Text> : null}

          <TouchableOpacity
            style={[styles.acceptBtn, (saving || dismissing) && { opacity: 0.6 }]}
            onPress={handleAccept}
            disabled={saving || dismissing}
          >
            {saving
              ? <ActivityIndicator size="small" color="#000" />
              : <Text style={styles.acceptBtnText}>{TOS_UPDATE.acceptLabel}</Text>}
          </TouchableOpacity>
          <Text style={styles.finePrint}>{TOS_UPDATE.acceptFinePrint}</Text>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: C.BG, alignItems: 'center', justifyContent: 'center', padding: 20 },
  card: {
    width: '100%', maxWidth: 520, maxHeight: '86%',
    backgroundColor: C.CARD, borderRadius: 14, borderWidth: 1, borderColor: C.BORDER, padding: 20,
  },
  scroll: { paddingBottom: 4 },
  heading: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginBottom: 4 },
  intro: { color: C.MUTED, fontSize: 14, marginBottom: 16 },
  highlight: { flexDirection: 'row', marginBottom: 14 },
  highlightIcon: { marginTop: 2, marginRight: 10 },
  highlightText: { flex: 1 },
  highlightTitle: { color: C.GOLD, fontSize: 15, fontWeight: 'bold', marginBottom: 2 },
  highlightBody: { color: C.TEXT, fontSize: 14, lineHeight: 20 },
  divider: { height: 1, backgroundColor: C.BORDER, marginVertical: 14 },
  termsHeading: { color: '#fff', fontSize: 15, fontWeight: 'bold', marginBottom: 2 },
  termsIntro: { color: C.MUTED, fontSize: 13, marginBottom: 8 },
  termsPoint: { color: C.TEXT, fontSize: 13, lineHeight: 19, marginBottom: 6 },
  readFull: { color: C.GOLD, fontSize: 13, textDecorationLine: 'underline', marginTop: 6 },
  manageLink: { color: C.MUTED, fontSize: 12, textDecorationLine: 'underline', marginTop: 10 },
  error: { color: '#E74C3C', fontSize: 13, marginTop: 10 },
  acceptBtn: {
    backgroundColor: C.GOLD, borderRadius: 8, paddingVertical: 13,
    alignItems: 'center', justifyContent: 'center', marginTop: 16,
  },
  acceptBtnText: { color: '#000', fontSize: 15, fontWeight: 'bold' },
  finePrint: { color: C.MUTED, fontSize: 11, textAlign: 'center', marginTop: 8 },
});
